import React, { useState, useRef, useEffect } from 'react';
import { apiFetch, apiFetchRaw } from '../apiClient';
import { useApp } from '../appContext';

// Both halves of this page run against our own API. The Power Automate path is gone (Jeremy,
// 2026-07-27): extraction is the `ai` module running a versioned prompt from
// apps/api/src/ai/prompts, and Commit is the `intake` module writing to Azure SQL.
//
// The `REACT_APP_EXTRACT_SOURCE` / `REACT_APP_INTAKE_SOURCE` toggles that used to gate this are
// deleted rather than defaulted, along with `REACT_APP_SOW_FLOW_URL` / `REACT_APP_SOW_SAVE_URL`.
// Retiring the extraction flow also stops the SharePoint row it wrote as an internal side effect --
// the thing no code change here could previously prevent (STATUS.md Section 6).
//
// Consequence worth knowing: this page now requires a reachable `apps/api`. There is no fallback.

/**
 * Read the NDJSON stream from POST /sow-extractions/stream, surfacing the model's reasoning as log
 * lines and returning the extracted fields.
 *
 * The reasoning arrives as many small deltas -- rendering each one as its own line produces
 * unreadable confetti. Instead we buffer and flush on sentence boundaries, so the log reads like
 * someone narrating their way through the contract.
 */
async function readExtractionStream(response, setLogLines) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';   // incomplete NDJSON line
  let thinking = ''; // reasoning text not yet flushed as a log line
  let fields = null;

  const flushSentences = (force) => {
    // Split on sentence-ending punctuation followed by whitespace.
    const parts = thinking.split(/(?<=[.!?])\s+/);
    const trailing = force ? '' : parts.pop() ?? '';
    const complete = parts.map(s => s.trim()).filter(Boolean);
    if (complete.length) {
      setLogLines(prev => [
        ...prev,
        ...complete.map(text => ({ text, warn: false, done: false })),
      ].slice(-40)); // a long SOW produces a lot of reasoning; keep the tail readable
    }
    thinking = trailing;
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Everything before the last newline is a set of complete JSON objects.
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.trim()) continue;
      let event;
      try {
        event = JSON.parse(line);
      } catch {
        continue; // a partial or malformed line is not worth failing the whole extraction over
      }
      if (event.type === 'thinking') {
        thinking += event.text;
        if (thinking.length > 80) flushSentences(false);
      } else if (event.type === 'done') {
        fields = event.fields;
      } else if (event.type === 'error') {
        throw new Error(event.message);
      }
    }
  }

  flushSentences(true);

  if (!fields) {
    // The stream ended without a terminal event -- dropped connection, or the server died
    // mid-extraction. Say that rather than silently showing an empty review form.
    throw new Error('The extraction stopped before finishing. Please try again.');
  }
  return fields;
}
const FIELDS = [
  { key: 'SOW_ID',                    label: 'SOW ID / Contract #',        ai: true,  flag: false, multiline: false },
  { key: 'Customer_Name',             label: 'Customer Name',              ai: true,  flag: false, multiline: false },
  { key: 'Project_Name',              label: 'Project Name',               ai: true,  flag: false, multiline: false },
  { key: 'Project_Type',              label: 'Project Type',               ai: true,  flag: false, multiline: false },
  { key: 'Start_Date',                label: 'Start Date',                 ai: true,  flag: false, multiline: false },
  { key: 'Project_Duration',          label: 'Project Duration',           ai: true,  flag: false, multiline: false },
  { key: 'Total_Contract_Value',       label: 'Total Contract Value',       ai: true,  flag: false, multiline: false },
  { key: 'Software_Modules_In_Scope', label: 'Software Modules in Scope',  ai: true,  flag: false, multiline: true  },
  { key: 'Explicit_Exclusions',       label: 'Explicit Exclusions',        ai: true,  flag: false, multiline: true  },
  { key: 'Services_Included',         label: 'Services Included',          ai: true,  flag: false, multiline: true  },
  { key: 'Billing_Milestones',        label: 'Billing Milestones',         ai: true,  flag: false, multiline: true  },
  // Milestone_Names / Milestone_Amounts / Acceptance_Criteria are gone as of prompt v4 -- they were
  // three parallel lists the reviewer had to line up by eye. `Milestones` is an array of objects
  // rendered as a table below, one row per milestone, which is how the SOW prints it and how the
  // database now stores it. It is deliberately NOT in FIELDS: it isn't a text input.
  { key: 'Key_Assumptions',           label: 'Key Assumptions',            ai: true,  flag: false, multiline: true  },
  { key: 'Constraints',               label: 'Constraints',                ai: true,  flag: false, multiline: true  },
  { key: 'Customer_Responsibilities', label: 'Customer Responsibilities',  ai: true,  flag: false, multiline: true  },
  { key: 'Ambiguities',               label: 'Ambiguities',                ai: true,  flag: true,  multiline: true  },
  { key: 'Missing_Data',              label: 'Missing Data',               ai: true,  flag: true,  multiline: true  },
  { key: 'Risk_Indicators',           label: 'Risk Indicators',            ai: true,  flag: true,  multiline: true  },
  // Required, and a real date rather than free text. The AI's `Start_Date` above stays exactly as
  // extracted -- a SOW routinely states only relative timing ("30 days from execution") or nothing,
  // and the prompt deliberately returns "" rather than guessing. This is the reviewer's estimate, and
  // it's what Project.startDate is set from: suggested phase and milestone dates when this SOW is
  // promoted into a schedule all hang off it. An estimate that gets corrected later beats a null.
  { key: 'Estimated_Start_Date',      label: 'Estimated Start Date',       ai: false, flag: false, multiline: false, required: true, dateInput: true },
  // Required: can't commit without capturing who reviewed the intake. Rendered as a user picker
  // (see `key === 'Reviewer'` in the review phase below), defaulting to the signed-in user -- still
  // required and still changeable, since the person running the extraction isn't always the reviewer
  // of record. `fields.ReviewerId` (not in this list -- it isn't a text field) carries the actual
  // user id the checklist's "Review SOW" item gets completed under on commit (item 8).
  { key: 'Reviewer',                  label: 'Reviewer',                   ai: false, flag: false, multiline: false, required: true },
];

// Every field marked `required` gates the Commit button. Derived rather than hardcoded so adding a
// required field is a one-line change to FIELDS above.
const REQUIRED_FIELDS = FIELDS.filter(f => f.required);

// yyyy-mm-dd for <input type="date">, which silently shows nothing for any other format.
// Returns '' for prose the SOW may have given instead of a date ("30 days from execution"), for an
// empty extraction, and for anything Date can't parse -- a blank required field the reviewer must
// fill is honest; a fabricated date is not.
function toDateInputValue(value) {
  if (!value || !String(value).trim()) return '';
  const trimmed = String(value).trim();
  // Already the target format -- return it verbatim rather than round-tripping through Date.
  // Not an optimization: `new Date('2026-08-01')` parses as UTC midnight (the ISO-8601 date-only
  // rule), and the local getters below then read it as the *previous* day anywhere west of
  // Greenwich. Prompt v3 makes ISO the normal shape of this field, so that path is now the common
  // one; v2's MM/DD/YYYY parsed as local time and never hit it.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  // Local getters, not toISOString(): the latter shifts to UTC and can land on the previous day.
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
}

// ── Typed extraction fields (prompt v3) ────────────────────────────────────
// Three fields stopped being strings: Total_Contract_Value and Milestone_Amounts are money and
// Start_Date is a date. The Review screen still edits everything as text -- a reviewer correcting a
// figure types digits, and a <textarea> is the right control for a per-milestone list -- so values
// are widened to strings on the way in and narrowed back on the way out.

// A number, or '' when the SOW stated none. Renders 0 as "0", which is a real amount, not a blank.
// Also handles the string a Prisma Decimal serializes to, so the same helper works on a milestone
// read back from the API.
export function moneyToText(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

// '' -> null (the SOW states no amount), not 0. Anything unparseable is passed through untouched so
// the API's validation rejects it with a message, rather than being silently nulled here.
function textToMoney(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;
  const stripped = trimmed.replace(/[$\s,]/g, '');
  return /^-?\d+(\.\d+)?$/.test(stripped) ? Number(stripped) : text;
}

// The milestone table's rows, normalized for editing: every cell a string, so an <input> can hold
// it and a half-typed figure doesn't have to round-trip through a number.
function milestonesToRows(value) {
  if (!Array.isArray(value)) return [];
  return value.map(m => ({
    number: m?.number ?? '',
    name: m?.name ?? '',
    amount: moneyToText(m?.amount),
    acceptanceCriteria: m?.acceptanceCriteria ?? '',
  }));
}

// Back to the wire shape. Rows with no name are dropped -- they're an empty row someone added and
// didn't fill in, not a milestone. Blank amounts become null, which is distinct from 0.
export function rowsToMilestones(rows) {
  return (rows || [])
    .filter(r => (r.name || '').trim())
    .map(r => ({
      number: (r.number || '').trim(),
      name: r.name.trim(),
      amount: textToMoney(r.amount),
      acceptanceCriteria: (r.acceptanceCriteria || '').trim(),
    }));
}

// Subtotal and total rows are part of the SOW's table and are extracted deliberately, but they
// aren't deliverables -- the promotion skips them by name. Flagging them in the UI stops a reviewer
// wondering why the amounts don't add up to the total shown two rows down.
export function isTotalRow(name) {
  return /^(sub\s*-?\s*totals?|totals?|grand\s*totals?)\b/i.test((name || '').trim());
}

const SECTIONS = [
  { title: 'Client & Contract',       keys: ['SOW_ID','Customer_Name','Project_Name','Project_Type','Start_Date','Estimated_Start_Date','Project_Duration','Total_Contract_Value'] },
  { title: 'Scope & Modules',         keys: ['Software_Modules_In_Scope','Explicit_Exclusions','Services_Included'] },
  // `milestoneTable: true` renders the per-milestone table after this section's normal fields.
  { title: 'Milestones & Financials', keys: ['Billing_Milestones'], milestoneTable: true },
  { title: 'Obligations',             keys: ['Key_Assumptions','Constraints','Customer_Responsibilities'] },
  { title: 'AI Risk Flags',           keys: ['Ambiguities','Missing_Data','Risk_Indicators'] },
  { title: 'Assignment',              keys: ['Reviewer'] },
];

// The fields SOWIntake overwrites unconditionally today when committing against a placeholder --
// `Project.name`/`Project.startDate` via promotePlaceholder, and every ProjectSowDetails column via
// an unconditional upsert (see intake.service.ts). Conflict resolution (item 7) only ever asks about
// these, and only where the placeholder actually disagrees -- committing against a blank field, or
// one that already matches, needs no decision.
function conflictFieldDefs(existingProject) {
  return [
    { key: 'Project_Name', label: 'Project Name', existingValue: existingProject?.name || '' },
    { key: 'Customer_Name', label: 'Customer', existingValue: existingProject?.account?.name || '' },
    { key: 'Estimated_Start_Date', label: 'Start Date', existingValue: toDateInputValue(existingProject?.startDate) },
    { key: 'Software_Modules_In_Scope', label: 'Software Modules In Scope', existingValue: existingProject?.sowDetails?.softwareModules || '' },
    { key: 'Services_Included', label: 'Services Included', existingValue: existingProject?.sowDetails?.services || '' },
    { key: 'Missing_Data', label: 'Missing Data', existingValue: existingProject?.sowDetails?.missingData || '' },
    { key: 'Risk_Indicators', label: 'Risk Indicators', existingValue: existingProject?.sowDetails?.riskIndicators || '' },
  ];
}

// `lockedPlaceholderId`/`existingProject`/`onDone` are set only when opened from a specific
// placeholder's Project Setup detail view (PlaceholderActionsPanel's "Load SOW", item 7) rather than
// the old standalone tab: the target is already known, so the picker/suggestion below is skipped
// entirely, and a real conflict step replaces the blind "SOW always wins" overwrite.
function SOWIntake({ lockedPlaceholderId, existingProject, onDone } = {}) {
  const { currentUser } = useApp();
  const [phase, setPhase] = useState('upload'); // upload | extracting | review | conflicts | confirmed
  const [fileName, setFileName] = useState('');
  const [fileData, setFileData] = useState(null);
  const [logLines, setLogLines] = useState([]);
  const [fields, setFields] = useState({});
  // The milestone table, held separately from `fields` because it's an array of rows rather than a
  // flat text value. Every cell is a string while being edited; `rowsToMilestones` narrows on submit.
  const [milestoneRows, setMilestoneRows] = useState([]);
  const [error, setError] = useState('');
  // What the automatic schedule promotion did, reported on the confirmation screen. Null until a
  // commit returns. A promotion failure never fails the commit, so this can be `{ ok: false }` on
  // an otherwise successful submission -- which is exactly the case worth telling the user about.
  const [promotion, setPromotion] = useState(null);
  // Placeholder projects this SOW could belong to. Committing against one promotes it in place
  // (ADR 0032) rather than creating a second project beside the forecast someone already built.
  // `null` means the reviewer hasn't touched the picker, so the suggestion below still applies;
  // `''` is an explicit "none of these, this is net-new". Unused entirely when locked.
  const [placeholders, setPlaceholders] = useState([]);
  const [chosenPlaceholderId, setChosenPlaceholderId] = useState(null);
  const [users, setUsers] = useState([]);
  // Per-field choice on the conflicts step: 'existing' or 'new'. Absent means 'new' -- committing
  // against a placeholder has always meant the SOW wins, so that's the default, not silence.
  const [resolutions, setResolutions] = useState({});
  const fileInputRef = useRef();

  // Which required fields are still blank. Gates the Commit button and names the first one in its
  // label, so the reason it's disabled is on the button itself rather than something to hunt for.
  const missingRequired = REQUIRED_FIELDS.filter(f => !(fields[f.key] || '').trim());

  // Refetched after every commit, not just on mount: a promoted placeholder is no longer one, and a
  // stale list would offer it again for a second SOW (the API would reject it, as a "Save failed").
  const loadPlaceholders = () =>
    apiFetch('/projects')
      .then(all => setPlaceholders(all.filter(p => p.isPlaceholder && !p.mergedIntoProjectId)))
      // A picker that failed to load must not block a commit -- the SOW can still land as net-new,
      // and the placeholder can be folded in afterwards from Configuration → Projects.
      .catch(() => setPlaceholders([]));

  useEffect(() => {
    // Locked means the target is already fixed -- the picker/suggestion list would just be unused.
    if (!lockedPlaceholderId) loadPlaceholders();
    apiFetch('/users').then(setUsers).catch(() => setUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default the Reviewer to whoever's signed in, once -- still a real field the reviewer can change,
  // not a hidden value, since the person running the extraction isn't always the reviewer of record.
  useEffect(() => {
    if (currentUser && !fields.ReviewerId) {
      setFields(prev => ({ ...prev, Reviewer: prev.Reviewer || currentUser.name, ReviewerId: currentUser.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Every path that leaves this SOW behind has to drop the pick with it. Carrying it into the next
  // SOW would promote the previous SOW's placeholder -- renaming it and reparenting its forecast,
  // silently, since an explicit choice also suppresses the customer-matched suggestion.
  function clearSowState() {
    setChosenPlaceholderId(null);
    setMilestoneRows([]);
    setResolutions({});
    setError('');
  }

  // Suggest, don't decide: exactly one placeholder on the customer the SOW names is unambiguous
  // enough to preselect, but the reviewer still sees it and can change it. Two candidates on the
  // same customer, or none, and the picker starts empty.
  const isOnSowCustomer = p =>
    (p.account?.name || '').trim().toLowerCase() === (fields.Customer_Name || '').trim().toLowerCase();
  const customerMatches = placeholders.filter(isOnSowCustomer);
  const suggestedPlaceholderId = customerMatches.length === 1 ? customerMatches[0].id : '';
  const placeholderId = lockedPlaceholderId || (chosenPlaceholderId ?? suggestedPlaceholderId);

  // Only the fields where the placeholder already has a non-blank value that actually disagrees with
  // what the SOW says -- a blank placeholder field, or one that already matches, is just a merge.
  const conflicts = lockedPlaceholderId
    ? conflictFieldDefs(existingProject)
        .map(d => ({ ...d, newValue: fields[d.key] || '' }))
        .filter(d => d.existingValue.trim() && d.newValue.trim()
          && d.existingValue.trim().toLowerCase() !== d.newValue.trim().toLowerCase())
    : [];

  // The canned LOG_STEPS list that used to live here is gone with the Power Automate path. It was
  // theater for a flow that reported no progress at all — invented step names on fixed timers,
  // which is precisely the wrong thing to show next to a real extraction whose actual reasoning
  // now streams into the same panel.

  // PDF only. Shared by the file picker and the drop zone: the input's `accept` attribute filters
  // the browse dialog but does nothing for drag-and-drop, so without this check a dragged .docx
  // would sail through to the API and be rejected there with a less obvious message.
  function acceptFile(file) {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      setError(`${file.name} isn't a PDF. SOW extraction reads PDFs only — please convert the document to PDF and try again.`);
      setFileName('');
      setFileData(null);
      return;
    }
    setFileName(file.name);
    setFileData(file);
    setError('');
  }

  function handleFileChange(e) {
    acceptFile(e.target.files[0]);
  }

  function handleDrop(e) {
    e.preventDefault();
    acceptFile(e.dataTransfer.files[0]);
  }

  function handleDragOver(e) { e.preventDefault(); }

  async function startExtraction() {
    if (!fileData) return;
    setPhase('extracting');
    setLogLines([]);
    setError('');

    // No canned progress animation any more. It existed because the Power Automate flow gave no
    // progress signal at all; the API streams the model's actual reasoning instead, and running
    // both would mean two competing sets of log lines.

    // Convert file to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(fileData);
    });

    try {
      // Streaming matters here: extraction takes 90-120 seconds on a real SOW. A silent connection
      // for two minutes reads as a hang, and users re-upload. Instead we render the model's own
      // reasoning as it arrives.
      //
      // apiFetchRaw, not apiFetch: apiFetch reads and parses the body for you, and calling .json()
      // on an NDJSON stream throws on the second line. Streaming needs the untouched Response.
      // The acting user comes from the bearer token apiFetchRaw attaches and is recorded
      // server-side on the AiInteractionLog row -- there's no client-supplied `submittedBy`.
      const response = await apiFetchRaw('/sow-extractions/stream', {
        method: 'POST',
        body: JSON.stringify({ fileName: fileData.name, fileContent: base64 }),
      });
      if (!response.ok) {
        // Validation failures (not a PDF, too large, not configured) come back as normal error
        // responses before streaming starts. Surface the message, not a status code.
        const body = await response.json().catch(() => null);
        const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message;
        throw new Error(message || 'API returned ' + response.status);
      }
      const data = await readExtractionStream(response, setLogLines);

      // Updater form, not a fresh object: Reviewer/ReviewerId (not an extracted field) must survive
      // an extraction that runs after the reviewer's already been defaulted or hand-picked.
      setFields(prev => ({
        Reviewer:                  prev.Reviewer || currentUser?.name || '',
        ReviewerId:                prev.ReviewerId || currentUser?.id || '',
        SOW_ID:                    data.SOW_ID                    || '',
        Customer_Name:             data.Customer_Name             || '',
        Project_Name:              data.Project_Name              || '',
        Project_Type:              data.Project_Type              || '',
        Start_Date:                data.Start_Date                || '',
        Project_Duration:          data.Project_Duration          || '',
        // A number, or null when the SOW states no total (prompt v3) -- widened to text to edit.
        Total_Contract_Value:      moneyToText(data.Total_Contract_Value),
        Software_Modules_In_Scope: data.Software_Modules_In_Scope || '',
        Explicit_Exclusions:       data.Explicit_Exclusions       || '',
        Services_Included:         data.Services_Included         || '',
        Billing_Milestones:        data.Billing_Milestones        || '',
        Key_Assumptions:           data.Key_Assumptions           || '',
        Constraints:               data.Constraints               || '',
        Customer_Responsibilities: data.Customer_Responsibilities || '',
        Ambiguities:               data.Ambiguities               || '',
        Missing_Data:              data.Missing_Data              || '',
        Risk_Indicators:           data.Risk_Indicators           || '',
        // Prefilled only when the SOW gave a date a date input can actually hold. Relative phrasing
        // ("30 days from execution") deliberately leaves this blank rather than resolving it to
        // something the SOW never said -- the reviewer picks it, and the extracted text stays on
        // screen underneath the field.
        Estimated_Start_Date:      toDateInputValue(data.Start_Date),
      }));
      setMilestoneRows(milestonesToRows(data.Milestones));
      // Straight to Review. The log has been live for the whole 90+ second wait, so there is no
      // animation left to finish -- the 6.5s pause that used to be here existed only to let the
      // canned Power Automate progress steps play out.
      setPhase('review');

    } catch (err) {
      setError('Extraction failed — ' + err.message + '. You can enter fields manually below.');
      setFields(prev => ({
        ...Object.fromEntries(FIELDS.map(f => [f.key, ''])),
        Reviewer: prev.Reviewer || currentUser?.name || '',
        ReviewerId: prev.ReviewerId || currentUser?.id || '',
      }));
      setMilestoneRows([]);
      setPhase('review');
    }
  }

  function handleFieldChange(key, value) {
    setFields(prev => ({ ...prev, [key]: value }));
  }

async function handleSubmit() {
  // Field NAMES match CreateIntakeSubmissionDto exactly; only the three typed fields are narrowed
  // back from the text the Review screen edits (prompt v3).
  //
  // Committing also builds the schedule: the API promotes the SOW into Phases/Milestones/Tasks as
  // a second step after the commit transaction. A promotion failure is reported but does NOT fail
  // the commit, so `promotion.ok === false` still means the SOW is saved. Milestone duplicates
  // against an existing placeholder are already skipped there too, by title
  // (SchedulePromotionService's own `existingMilestoneTitles` check) -- nothing extra needed here.
  try {
    // The conflicts step (if it ran) only recorded a *choice* per field -- apply it here so the
    // fields actually posted reflect "keep existing" where that's what was picked. Everything else
    // in `fields` (including ones with no conflict, or that were never in scope for one) is
    // untouched, so a net-new SOW with no locked placeholder behaves exactly as before.
    const resolvedFields = { ...fields };
    for (const c of conflicts) {
      if (resolutions[c.key] === 'existing') resolvedFields[c.key] = c.existingValue;
    }
    const body = {
      ...resolvedFields,
      Total_Contract_Value: textToMoney(resolvedFields.Total_Contract_Value),
      Milestones: rowsToMilestones(milestoneRows),
      // Omitted, not empty-string: the API validates this as a UUID when present.
      ...(placeholderId ? { Placeholder_Project_Id: placeholderId } : {}),
    };
    const result = await apiFetch('/intake-submissions', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    setPromotion(result?.promotion ?? null);
    setPhase('confirmed');
    if (!lockedPlaceholderId) loadPlaceholders();

  } catch (err) {
    alert('Save failed — ' + err.message);
  }
}

  // ── PHASE: UPLOAD ──────────────────────────────────────────
  if (phase === 'upload') return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Stage 1</div>
        <h1 className="page-title">SOW Intake</h1>
        <p className="page-subtitle">Upload a signed Statement of Work. The AI will extract all intake fields automatically.</p>
      </div>

      {/* Phase indicator */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:32 }}>
        {['Upload SOW','Review & Edit','Commit'].map((label, i) => (
          <React.Fragment key={i}>
            <div style={{
              padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:600,
              background: i===0 ? 'var(--primary-dark)' : 'var(--surface)',
              color: i===0 ? 'var(--white)' : 'var(--text-mid)',
              border: '1px solid var(--border)',
            }}>{i+1}. {label}</div>
            {i < 2 && <span style={{ color:'var(--text-mid)', fontSize:12 }}>→</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Upload zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current.click()}
        style={{
          border: `2px dashed ${fileName ? 'var(--teal)' : 'var(--border)'}`,
          borderRadius: 12,
          padding: '56px 40px',
          textAlign: 'center',
          cursor: 'pointer',
          background: fileName ? 'rgba(0,167,157,0.05)' : 'var(--surface)',
          transition: 'all 0.2s',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 14 }}>{fileName ? '📋' : '📄'}</div>
        <div style={{ fontFamily:'Georgia', fontSize:18, fontWeight:'bold', color:'var(--heading)', marginBottom:6 }}>
          {fileName ? 'SOW ready for extraction' : 'Drop your SOW here, or click to browse'}
        </div>
        <div style={{ fontSize:13, color:'var(--text-mid)', marginBottom: fileName ? 12 : 0 }}>
          {fileName ? fileName : 'PDF only · The AI will extract all intake fields automatically'}
        </div>
        {fileName && (
          <span style={{
            display:'inline-flex', alignItems:'center', gap:6,
            padding:'6px 14px', borderRadius:8,
            background:'rgba(0,167,157,0.12)', border:'1px solid var(--teal)',
            fontSize:12, fontWeight:600, color:'var(--teal)',
          }}>✓ {fileName}</span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx"
          style={{ display:'none' }}
          onChange={handleFileChange}
          onClick={e => e.stopPropagation()}
        />
      </div>

      <button
        className="btn btn-primary"
        style={{ width:'100%', padding:14, fontSize:14, justifyContent:'center' }}
        disabled={!fileName}
        onClick={startExtraction}
      >
        {fileName ? 'Extract Intake Fields →' : '↑ Upload a SOW to continue'}
      </button>
    </div>
  );

  // ── PHASE: EXTRACTING ──────────────────────────────────────
  if (phase === 'extracting') return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Stage 1</div>
        <h1 className="page-title">SOW Intake</h1>
      </div>

      <div className="card" style={{ maxWidth:560, margin:'0 auto', textAlign:'center', padding:40 }}>
        <div style={{ fontSize:36, marginBottom:16 }}>⏳</div>
        <div style={{ fontFamily:'Georgia', fontSize:20, fontWeight:'bold', color:'var(--heading)', marginBottom:6 }}>
          Reading your SOW…
        </div>
        <div style={{ fontSize:13, color:'var(--text-mid)', marginBottom:28 }}>
          {/* Honest number. A real SOW measured 57s and 106s; promising 15-30 sets people up to
              think it has hung at the one-minute mark. */}
          The AI is reading the contract end to end. This usually takes one to two minutes — its
          notes appear below as it works.
        </div>

        {/* Log lines. On the streamed path these are the model's own reasoning, arriving live. */}
        <div style={{ textAlign:'left', background:'var(--light-bg)', borderRadius:8, padding:16, maxHeight:320, overflowY:'auto' }}>
          {logLines.map((line, i) => (
            <div key={i} style={{
              fontSize:12, marginBottom:6,
              color: line.warn ? 'var(--yellow)' : 'var(--teal)',
              display:'flex', gap:8,
            }}>
              <span>{line.warn ? '⚠' : '→'}</span>
              <span>{line.text}</span>
            </div>
          ))}
          {/* A live "working" line for the whole extraction. It used to be conditional on the
              canned step list still having steps left, which meant it vanished as soon as enough
              reasoning had arrived -- leaving a static log and no sign of activity. */}
          <div style={{ fontSize:12, color:'var(--primary-light)', display:'flex', gap:8 }}>
            <span>•</span>
            <span>{logLines.length ? 'Still reading…' : 'Sending the document to the model…'}</span>
          </div>
        </div>

        {/* Violet accent bar */}
        <div style={{
          height:3, background:'var(--violet)',
          borderRadius:2, marginTop:24,
          animation:'pulse 1.5s ease-in-out infinite',
        }} />
      </div>
    </div>
  );

  // ── PHASE: REVIEW ──────────────────────────────────────────
  if (phase === 'review') return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Stage 1</div>
        <h1 className="page-title">Review Extracted Fields</h1>
        <p className="page-subtitle">Correct anything that looks wrong before committing to the database.</p>
      </div>

      {/* Legend */}
      <div style={{ display:'flex', gap:16, marginBottom:24, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-mid)' }}>
          <div style={{ width:12, height:12, borderRadius:2, border:'2px solid var(--violet)' }} />
          AI extracted
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-mid)' }}>
          <div style={{ width:12, height:12, borderRadius:2, border:'2px solid var(--yellow)' }} />
          Needs review
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-mid)' }}>
          <div style={{ width:12, height:12, borderRadius:2, border:'2px solid var(--border)' }} />
          Manual entry
        </div>
      </div>

      {error && (
        <div style={{
          padding:'12px 16px', borderRadius:8, marginBottom:20,
          background:'rgba(239,65,54,0.08)', border:'1px solid rgba(239,65,54,0.3)',
          fontSize:13, color:'var(--red)',
        }}>⚠ {error}</div>
      )}

      {/* AI flags notice */}
      {(fields.Ambiguities || fields.Missing_Data || fields.Risk_Indicators) && (
        <div style={{
          padding:'12px 16px', borderRadius:8, marginBottom:20,
          background:'rgba(251,176,64,0.08)', border:'1px solid rgba(251,176,64,0.3)',
          fontSize:13, color:'var(--warn-text)',
        }}>
          ⚠ The AI identified ambiguities, missing data, or risk indicators in this SOW. Review the flagged fields carefully before submitting.
        </div>
      )}

      {/* Field sections */}
      {SECTIONS.map(section => (
        <div key={section.title} className="card" style={{ marginBottom:16 }}>
          <div className="card-title">{section.title}</div>
          <div className="grid-2" style={{ gap:16 }}>
            {section.keys.map(key => {
              const fieldDef = FIELDS.find(f => f.key === key);
              const borderColor = fieldDef.flag ? 'var(--yellow)' : fieldDef.ai ? 'var(--violet)' : 'var(--border)';
              return (
                <div key={key} className="form-group" style={{ marginBottom:0,
                  gridColumn: fieldDef.multiline ? 'span 2' : 'span 1'
                }}>
                  <label className="form-label" style={{ display:'flex', alignItems:'center', gap:6 }}>
                    {fieldDef.label}
                    {fieldDef.flag && <span className="badge badge-yellow" style={{ fontSize:10 }}>Review</span>}
                    {fieldDef.ai && !fieldDef.flag && <span className="badge badge-violet" style={{ fontSize:10 }}>AI</span>}
                    {!fieldDef.ai && <span className="badge badge-gray" style={{ fontSize:10 }}>{fieldDef.required ? 'Required' : 'Manual'}</span>}
                  </label>
                  {key === 'Reviewer' ? (
                    <select
                      className="form-select"
                      style={{ borderColor, borderWidth:2 }}
                      value={fields.ReviewerId || ''}
                      onChange={e => {
                        const picked = users.find(u => u.id === e.target.value);
                        setFields(prev => ({ ...prev, ReviewerId: e.target.value, Reviewer: picked?.name || '' }));
                      }}
                    >
                      <option value="">— Select reviewer —</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  ) : fieldDef.multiline ? (
                    <textarea
                      className="form-textarea"
                      style={{ borderColor, borderWidth:2 }}
                      value={fields[key] || ''}
                      onChange={e => handleFieldChange(key, e.target.value)}
                      rows={4}
                    />
                  ) : (
                    <input
                      type={fieldDef.dateInput ? 'date' : 'text'}
                      className="form-input"
                      style={{ borderColor, borderWidth:2 }}
                      value={fields[key] || ''}
                      onChange={e => handleFieldChange(key, e.target.value)}
                    />
                  )}
                  {/* What the SOW actually said about timing, kept in view next to the estimate the
                      reviewer has to enter -- often it's relative ("30 days from execution") or
                      absent, which is exactly why the estimate is a separate required field. */}
                  {key === 'Estimated_Start_Date' && (
                    <div style={{ fontSize:11, color:'var(--text-mid)', marginTop:4 }}>
                      {fields.Start_Date && fields.Start_Date.trim()
                        ? <>SOW says: "{fields.Start_Date}"</>
                        : 'The SOW states no calendar start date — enter your best estimate. '
                          + 'Any relative timing it did give ("30 days from execution") is quoted '
                          + 'under Missing Data below.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* One row per milestone, mirroring the SOW's own payment table. Replaces the three
              parallel textareas a reviewer used to have to line up by eye. */}
          {section.milestoneTable && (
            <MilestoneTable rows={milestoneRows} onChange={setMilestoneRows} />
          )}
        </div>
      ))}

      {/* Which existing placeholder this SOW belongs to, if any. Sits immediately above Commit
          because it changes what Commit does: promote that row, or create a new project. Skipped
          entirely when opened from a specific placeholder (item 7) -- the target is already fixed. */}
      {!lockedPlaceholderId && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-title">Placeholder project</div>
          <div style={{ fontSize:13, color:'var(--text-mid)', marginBottom:12 }}>
            If resourcing already forecast this work before the SOW was signed, pick that placeholder.
            It becomes this project — its forecast, team plan and baselines carry over untouched.
            Leave it as “Create a new project” if nothing was forecast in advance.
          </div>
          <div className="form-group" style={{ marginBottom:0, maxWidth:520 }}>
            <select
              className="form-select"
              value={placeholderId}
              onChange={e => setChosenPlaceholderId(e.target.value)}
            >
              <option value="">— Create a new project —</option>
              {placeholders.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.account?.name || 'No account'}
                  {/* Promoting reparents the row to the SOW's customer and takes its forecast along,
                      with no second row to undo from -- so a pick from another customer is called out
                      rather than reading like any other option. */}
                  {isOnSowCustomer(p) ? '' : ' — different customer'}
                </option>
              ))}
            </select>
            {placeholderId && !isOnSowCustomer(placeholders.find(p => p.id === placeholderId) || {}) && (
              <div style={{ fontSize:12, color:'var(--red)', marginTop:6 }}>
                ⚠ That placeholder belongs to a different customer. Committing moves it, and its
                forecast, to {fields.Customer_Name || 'this SOW’s customer'}.
              </div>
            )}
            {placeholderId && placeholderId === suggestedPlaceholderId && chosenPlaceholderId === null && (
              <div style={{ fontSize:11, color:'var(--text-mid)', marginTop:4 }}>
                Suggested — the only placeholder on {fields.Customer_Name}.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Submit row */}
      <div className="divider" />
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <button className="btn btn-outline" onClick={() => { clearSowState(); setPhase('upload'); }}>← Re-upload SOW</button>
        <button
          className="btn btn-success"
          disabled={missingRequired.length > 0}
          onClick={() => (conflicts.length > 0 ? setPhase('conflicts') : handleSubmit())}
        >
          {missingRequired.length > 0
            ? `Enter ${missingRequired[0].label} to continue`
            : conflicts.length > 0
              ? `Resolve ${conflicts.length} difference${conflicts.length === 1 ? '' : 's'} →`
              : placeholderId ? 'Commit & promote placeholder →' : 'Commit →'}
        </button>
      </div>
    </div>
  );

  // ── PHASE: CONFLICTS (item 7 -- only reachable with a locked placeholder and real conflicts) ──
  if (phase === 'conflicts') return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Stage 1</div>
        <h1 className="page-title">Resolve differences</h1>
        <p className="page-subtitle">
          This placeholder already has a value for {conflicts.length === 1 ? 'this field' : 'these fields'} that
          disagrees with the SOW. Pick which one to keep — everything else merges automatically.
        </p>
      </div>

      {conflicts.map(c => (
        <div key={c.key} className="card" style={{ marginBottom:16 }}>
          <div className="card-title">{c.label}</div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <label style={{ display:'flex', gap:10, alignItems:'flex-start', cursor:'pointer' }}>
              <input
                type="radio"
                name={`resolve-${c.key}`}
                checked={(resolutions[c.key] || 'new') === 'existing'}
                onChange={() => setResolutions(prev => ({ ...prev, [c.key]: 'existing' }))}
                style={{ marginTop:3 }}
              />
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text-mid)' }}>Keep existing</div>
                <div style={{ fontSize:13, whiteSpace:'pre-wrap' }}>{c.existingValue}</div>
              </div>
            </label>
            <label style={{ display:'flex', gap:10, alignItems:'flex-start', cursor:'pointer' }}>
              <input
                type="radio"
                name={`resolve-${c.key}`}
                checked={(resolutions[c.key] || 'new') === 'new'}
                onChange={() => setResolutions(prev => ({ ...prev, [c.key]: 'new' }))}
                style={{ marginTop:3 }}
              />
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text-mid)' }}>Use new from SOW</div>
                <div style={{ fontSize:13, whiteSpace:'pre-wrap' }}>{c.newValue}</div>
              </div>
            </label>
          </div>
        </div>
      ))}

      <div className="divider" />
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <button className="btn btn-outline" onClick={() => setPhase('review')}>← Back to review</button>
        <button className="btn btn-success" onClick={handleSubmit}>
          {placeholderId ? 'Commit & promote placeholder →' : 'Commit →'}
        </button>
      </div>
    </div>
  );

  // ── PHASE: CONFIRMED ───────────────────────────────────────
  if (phase === 'confirmed') return (
    <div style={{ maxWidth:640, margin:'0 auto', textAlign:'center', paddingTop:48 }}>
      <div style={{
        width:72, height:72, borderRadius:'50%',
        background:'rgba(0,124,100,0.1)', border:'2px solid rgba(0,124,100,0.3)',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:32, margin:'0 auto 24px',
      }}>✓</div>
      <h2 style={{ fontFamily:'Georgia', fontSize:22, color:'var(--heading)', marginBottom:8 }}>
        Intake record submitted
      </h2>
      <p style={{ fontSize:14, color:'var(--text-mid)', marginBottom:32 }}>
        The record has been committed to the database and its schedule built. Open Project Schedule
        to review the phases, milestones and tasks that were created.
      </p>

      <div className="grid-3" style={{ marginBottom:32, textAlign:'left' }}>
        {[
          { icon:'📋', title:'Database record', desc:'All fields committed to Pulse', status:'● Saved', color:'var(--green)' },
          // The real outcome of the automatic promotion, not a canned status. A failure here still
          // leaves the SOW committed, so it says so rather than reading as a lost submission.
          promotion?.ok
            ? {
                icon:'📅',
                title:'Schedule built',
                desc:`${promotion.created?.phases ?? 0} phases, `
                   + `${promotion.created?.milestones ?? 0} milestones, `
                   + `${promotion.created?.tasks ?? 0} tasks created`,
                status: promotion.warnings?.length
                  ? `▲ ${promotion.warnings.length} warning${promotion.warnings.length === 1 ? '' : 's'}`
                  : '● Created',
                color: promotion.warnings?.length ? 'var(--yellow)' : 'var(--green)',
              }
            : {
                icon:'📅',
                title:'Schedule not built',
                desc:'The SOW is saved. Promote it by hand from Project Schedule.',
                status:'▲ Needs attention',
                color:'var(--yellow)',
              },
          // Honest rather than decorative: nothing notifies anyone yet.
          { icon:'📬', title:'PM notification', desc:'Not built yet — no notification was sent', status:'○ Not implemented', color:'var(--text-mid)' },
        ].map((item, i) => (
          <div key={i} className="card">
            <div style={{ fontSize:24, marginBottom:8 }}>{item.icon}</div>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--heading)', marginBottom:4 }}>{item.title}</div>
            <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:10 }}>{item.desc}</div>
            <div style={{ fontSize:11, fontWeight:700, color:item.color }}>{item.status}</div>
          </div>
        ))}
      </div>

      {/* Promotion warnings are the things a PM has to act on -- an unassigned milestone, a line
          skipped as a subtotal, a duration that couldn't be parsed. Surfaced here rather than left
          for someone to notice a gap in the Gantt later. */}
      {promotion?.warnings?.length > 0 && (
        <div className="card" style={{ textAlign:'left', marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--heading)', marginBottom:8 }}>
            Schedule warnings
          </div>
          <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:'var(--text-mid)' }}>
            {promotion.warnings.map((w, i) => <li key={i} style={{ marginBottom:4 }}>{w}</li>)}
          </ul>
        </div>
      )}

      {promotion?.ok === false && promotion?.error && (
        <div className="card" style={{ textAlign:'left', marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--heading)', marginBottom:8 }}>
            Why the schedule wasn't built
          </div>
          <div style={{ fontSize:12, color:'var(--text-mid)' }}>{promotion.error}</div>
        </div>
      )}

      {lockedPlaceholderId ? (
        // The placeholder this ran against is now a real project -- there's nothing left to load a
        // second SOW against from here, so this hands back to Project Setup instead of resetting.
        <button className="btn btn-primary" onClick={() => onDone && onDone()}>
          Return to project setup →
        </button>
      ) : (
        <button
          className="btn btn-outline"
          onClick={() => {
            setPhase('upload'); setFileName(''); setFileData(null); setFields({});
            setPromotion(null); clearSowState();
          }}
        >
          Start new intake
        </button>
      )}
    </div>
  );
}

/**
 * The SOW's milestone table, editable.
 *
 * One row per milestone with the four columns the document itself uses — number, name, amount,
 * acceptance criteria — because that's how a reviewer reads it against the PDF, and since prompt v4
 * it's also how the database stores it (`Milestone.sowMilestoneNumber` / `.amount` /
 * `.acceptanceCriteria`). Before this, the same data was three parallel textareas that had to be
 * aligned by counting lines.
 *
 * Acceptance criteria get a textarea rather than an input: they routinely run to several sentences,
 * and they're the text a Deliverable Acceptance Sheet is drafted from, so they need to be readable
 * and correctable here rather than truncated to one line.
 */
export function MilestoneTable({ rows, onChange }) {
  const update = (i, key, value) =>
    onChange(rows.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)));

  const addRow = () =>
    onChange([...rows, { number: '', name: '', amount: '', acceptanceCriteria: '' }]);

  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  // Deliverable rows only. A subtotal's figure is the sum of the rows above it, so including it
  // would double the total and make the footer look wrong to anyone checking it against the SOW.
  const deliverableTotal = rows
    .filter(r => !isTotalRow(r.name))
    .reduce((sum, r) => {
      const n = Number(String(r.amount).replace(/[$\s,]/g, ''));
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);

  const missingCriteria = rows.filter(r => (r.name || '').trim() && !isTotalRow(r.name)
    && !(r.acceptanceCriteria || '').trim()).length;

  if (!rows.length) return (
    <div style={{ marginTop:16, padding:16, borderRadius:8, background:'var(--light-bg)',
      fontSize:13, color:'var(--text-mid)' }}>
      The AI found no milestone table in this SOW. Add rows by hand if the document has one —
      milestones drive the schedule, the billing plan, and the acceptance sign-offs.
      <div style={{ marginTop:10 }}>
        <button className="btn btn-outline" onClick={addRow}>+ Add milestone</button>
      </div>
    </div>
  );

  return (
    <div style={{ marginTop:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline',
        marginBottom:8 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--heading)' }}>
          Milestones
          <span className="badge badge-violet" style={{ fontSize:10, marginLeft:8 }}>AI</span>
        </div>
        <div style={{ fontSize:11, color:'var(--text-mid)' }}>
          {rows.length} row{rows.length === 1 ? '' : 's'} from the SOW's payment table
        </div>
      </div>

      {/* Horizontal scroll container: four columns including a textarea don't fit a narrow window,
          and the page itself must never scroll sideways. */}
      <div style={{ overflowX:'auto', border:'1px solid var(--border)', borderRadius:8 }}>
        <table style={{ borderCollapse:'collapse', width:'100%', minWidth:720, fontSize:12 }}>
          <thead>
            <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
              <th style={{ padding:'8px 10px', width:80,  fontWeight:600 }}>#</th>
              <th style={{ padding:'8px 10px',            fontWeight:600 }}>Milestone</th>
              <th style={{ padding:'8px 10px', width:120, fontWeight:600 }}>Amount (USD)</th>
              <th style={{ padding:'8px 10px',            fontWeight:600 }}>Acceptance criteria</th>
              <th style={{ padding:'8px 10px', width:36 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const total = isTotalRow(row.name);
              return (
                <tr key={i} style={{
                  borderTop:'1px solid var(--border)',
                  // Totals are visually set apart: they're in the table because the SOW prints them
                  // and the customer's payment schedule cites them, but they aren't deliverables and
                  // the promotion skips them.
                  background: total ? 'var(--light-bg)' : 'transparent',
                }}>
                  <td style={{ padding:'6px 8px', verticalAlign:'top' }}>
                    <input className="form-input" style={{ fontSize:12, padding:'5px 7px' }}
                      value={row.number}
                      onChange={e => update(i, 'number', e.target.value)} />
                  </td>
                  <td style={{ padding:'6px 8px', verticalAlign:'top' }}>
                    <input className="form-input" style={{ fontSize:12, padding:'5px 7px' }}
                      value={row.name}
                      onChange={e => update(i, 'name', e.target.value)} />
                    {total && (
                      <div style={{ fontSize:10, color:'var(--text-mid)', marginTop:3 }}>
                        Total row — kept for reference, not created as a milestone
                      </div>
                    )}
                  </td>
                  <td style={{ padding:'6px 8px', verticalAlign:'top' }}>
                    <input className="form-input" style={{ fontSize:12, padding:'5px 7px' }}
                      value={row.amount}
                      placeholder="—"
                      onChange={e => update(i, 'amount', e.target.value)} />
                  </td>
                  <td style={{ padding:'6px 8px', verticalAlign:'top' }}>
                    <textarea className="form-textarea"
                      style={{ fontSize:12, padding:'5px 7px', minHeight:52 }}
                      rows={2}
                      value={row.acceptanceCriteria}
                      placeholder={total ? '—' : 'None stated in the SOW'}
                      onChange={e => update(i, 'acceptanceCriteria', e.target.value)} />
                  </td>
                  <td style={{ padding:'6px 4px', verticalAlign:'top' }}>
                    <button className="btn btn-outline"
                      style={{ padding:'3px 7px', fontSize:11, lineHeight:1.2 }}
                      title="Remove this row"
                      onClick={() => removeRow(i)}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop:'2px solid var(--border)' }}>
              <td />
              <td style={{ padding:'8px 10px', fontWeight:600 }}>Deliverable rows total</td>
              <td style={{ padding:'8px 10px', fontWeight:600 }}>
                {deliverableTotal.toLocaleString('en-US')}
              </td>
              <td colSpan={2} style={{ padding:'8px 10px', fontSize:11, color:'var(--text-mid)' }}>
                Excludes total rows. Compare against Total Contract Value above.
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginTop:10, gap:12 }}>
        <button className="btn btn-outline" onClick={addRow}>+ Add milestone</button>
        {missingCriteria > 0 && (
          <div style={{ fontSize:11, color:'var(--warn-text)' }}>
            ⚠ {missingCriteria} milestone{missingCriteria === 1 ? '' : 's'} without acceptance
            criteria — a DAS can't be drafted for {missingCriteria === 1 ? 'it' : 'them'} yet.
          </div>
        )}
      </div>
    </div>
  );
}

export default SOWIntake;